import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { X, ZoomIn, ChevronLeft, ChevronRight, ZoomOut, RotateCcw, Download } from 'lucide-react';

interface GalleryImage {
  src: string;
  alt: string;
  caption?: string;
  size?: 'small' | 'medium' | 'large' | 'wide';
}

interface PremiumProjectGalleryProps {
  images: GalleryImage[];
}

export function PremiumProjectGallery({ images }: PremiumProjectGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!images || images.length === 0) return null;

  const openLightbox = useCallback((index: number) => {
    setSelectedIndex(index);
    setSelectedImage(images[index]);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [images]);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, []);

  const goToPrevious = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e && 'stopPropagation' in e) {
      e.stopPropagation();
    }
    const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
    setSelectedIndex(newIndex);
    setSelectedImage(images[newIndex]);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [selectedIndex, images]);

  const goToNext = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e && 'stopPropagation' in e) {
      e.stopPropagation();
    }
    const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
    setSelectedIndex(newIndex);
    setSelectedImage(images[newIndex]);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [selectedIndex, images]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleZoomIn = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoom(prev => Math.min(prev + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoom(prev => Math.max(prev - 0.5, 1));
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom(prev => Math.min(prev + 0.2, 4));
    } else {
      setZoom(prev => Math.max(prev - 0.2, 1));
    }
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, zoom, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      e.preventDefault();
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart || !selectedImage) return;

    if (e.touches.length === 1 && zoom === 1) {
      const deltaX = e.touches[0].clientX - touchStart.x;
      const deltaY = e.touches[0].clientY - touchStart.y;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          goToPrevious(e);
        } else {
          goToNext(e);
        }
        setTouchStart(null);
      }
    }
  }, [touchStart, zoom, goToPrevious, goToNext, selectedImage]);

  const handleTouchEnd = useCallback(() => {
    setTouchStart(null);
  }, []);

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedImage) {
      const link = document.createElement('a');
      link.href = selectedImage.src;
      link.download = `project-image-${selectedIndex + 1}.jpg`;
      link.target = '_blank';
      link.click();
    }
  }, [selectedImage, selectedIndex]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!selectedImage) return;

    switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'Escape':
        closeLightbox();
        break;
      case '+':
      case '=':
        setZoom(prev => Math.min(prev + 0.5, 4));
        break;
      case '-':
      case '_':
        setZoom(prev => Math.max(prev - 0.5, 1));
        break;
      case '0':
        resetZoom();
        break;
    }
  }, [selectedImage, goToPrevious, goToNext, closeLightbox, handleZoomIn, handleZoomOut, resetZoom]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const body = document.body;
    if (selectedImage) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = 'unset';
    }
    return () => {
      body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const getGridClass = (index: number, size?: string) => {
    const baseClasses = 'relative overflow-hidden group cursor-pointer rounded-2xl border border-white/5';

    if (size === 'large') return `${baseClasses} col-span-1 md:col-span-2 row-span-2`;
    if (size === 'wide') return `${baseClasses} col-span-1 md:col-span-2`;
    if (size === 'small') return `${baseClasses} col-span-1`;

    return `${baseClasses} col-span-1`;
  };

  const getAspectRatio = (size?: string) => {
    if (size === 'large') return 'aspect-square';
    if (size === 'wide') return 'aspect-video';
    if (size === 'small') return 'aspect-square';
    return 'aspect-[4/3]';
  };

  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-900/5 via-transparent to-transparent" />

      <div className="container mx-auto px-4 relative z-10">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Galeria do{" "}
            <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
              Projeto
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Explore os detalhes visuais e a excelência em cada pixel
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-[200px] md:auto-rows-[250px]">
          {images.map((image, index) => (
            <motion.div
              key={index}
              className={getGridClass(index, image.size)}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              onClick={() => openLightbox(index)}
              role="button"
              tabIndex={0}
              aria-label={`Abrir imagem ${index + 1} de ${images.length}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(index);
                }
              }}
            >
              <div className={`w-full h-full ${getAspectRatio(image.size)} overflow-hidden`}>
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  style={{ filter: 'brightness(0.9) contrast(1.1)' }}
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300" />

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="transform scale-0 group-hover:scale-100 transition-transform duration-300">
                    <ZoomIn className="w-12 h-12 text-white" />
                  </div>
                </div>

                {image.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-sm font-medium line-clamp-2">{image.caption}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-2 md:p-4"
              onClick={closeLightbox}
              role="dialog"
              aria-modal="true"
              aria-label="Lightbox de imagens"
            >
              <motion.button
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={closeLightbox}
                className="absolute top-4 md:top-6 right-4 md:right-6 z-50 p-2 md:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                aria-label="Fechar lightbox"
              >
                <X className="w-5 h-5 md:w-6 md:h-6" />
              </motion.button>

              <motion.button
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                onClick={goToPrevious}
                className="absolute left-2 md:left-6 z-50 p-2 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                aria-label="Imagem anterior"
              >
                <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
              </motion.button>

              <motion.button
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 20, opacity: 0 }}
                onClick={goToNext}
                className="absolute right-2 md:right-6 z-50 p-2 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                aria-label="Próxima imagem"
              >
                <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
              </motion.button>

              <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
                <motion.button
                  onClick={handleZoomOut}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                  aria-label="Diminuir zoom"
                  disabled={zoom <= 1}
                >
                  <ZoomOut className="w-4 h-4" />
                </motion.button>

                <span className="text-white text-sm font-medium min-w-[3rem] text-center">
                  {Math.round(zoom * 100)}%
                </span>

                <motion.button
                  onClick={handleZoomIn}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                  aria-label="Aumentar zoom"
                  disabled={zoom >= 4}
                >
                  <ZoomIn className="w-4 h-4" />
                </motion.button>

                {zoom > 1 && (
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    onClick={resetZoom}
                    className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-white rounded-full transition-all duration-300"
                    aria-label="Resetar zoom"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </motion.button>
                )}

                <div className="w-px h-4 bg-white/30" />

                <motion.button
                  onClick={handleDownload}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300"
                  aria-label="Baixar imagem"
                >
                  <Download className="w-4 h-4" />
                </motion.button>
              </div>

              <motion.div
                ref={containerRef}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative w-full max-w-6xl max-h-[85vh] md:max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
                style={{
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                role="img"
                aria-label={`${selectedImage.caption || selectedImage.alt} (${selectedIndex + 1} de ${images.length})`}
              >
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                  }}
                >
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full"
                      />
                    </div>
                  )}

                  <img
                    ref={imageRef}
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="max-w-full max-h-full object-contain rounded-lg"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                    draggable={false}
                  />
                </div>

                {selectedImage.caption && (
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/90 to-transparent rounded-b-lg pointer-events-none"
                  >
                    <p className="text-white text-base md:text-lg font-medium">{selectedImage.caption}</p>
                    <p className="text-gray-400 text-sm mt-2">
                      {selectedIndex + 1} de {images.length}
                    </p>
                  </motion.div>
                )}
              </motion.div>

              <div className="absolute top-4 md:top-6 left-1/2 -translate-x-1/2 z-50">
                <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <span className="text-white/50 text-xs">ESC</span>
                  <span className="text-white/70 text-xs">fechar</span>
                  <div className="w-px h-3 bg-white/30 mx-1" />
                  <span className="text-white/50 text-xs">←→</span>
                  <span className="text-white/70 text-xs">navegar</span>
                  <div className="w-px h-3 bg-white/30 mx-1" />
                  <span className="text-white/50 text-xs">+/-</span>
                  <span className="text-white/70 text-xs">zoom</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
